import { Component, OnChanges, OnInit, SimpleChanges, computed, inject, input, model, signal } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { ValidationPatterns } from "@bbwt/modules/validation";
import { BrowserInfoService } from "@bbwt/services/browser-info.service";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { PasswordModule } from "primeng/password";
import { RecaptchaConfiguration } from "ngx-captcha/lib/models/recaptcha-configuration";
import { ReCaptchaV3Service } from "ngx-captcha";
import { AccountService } from "@account/services";
import CryptoES from "crypto-es";
import { AppStorage } from "@bbwt/utils/app-storage";
import { LinkNhsMiproLoginResponse, LinkNhsMiproLoginRequest, LinkNhsMiproService, CompleteNhsLinkingStatus } from "./link-nhs-mipro.service";
import { MessageModule } from "primeng/message";
import { ActivatedRoute, Router } from "@angular/router";
import { firstValueFrom as fv, map } from "rxjs";
import { MiproSharedModule } from "@mipro/mipro-shared.module";
import { UserService } from "@main/users";
import { SecurityService } from "@bbwt/modules/security";

@Component({
    selector: "link-nhs-mipro-dialog",
    standalone: true,
    imports: [DialogModule, ReactiveFormsModule, ButtonModule, InputTextModule, PasswordModule, MessageModule, MiproSharedModule],
    providers: [LinkNhsMiproService],
    templateUrl: "./link-nhs-mipro-dialog.component.html"
})
export class LinkNhsMiproDialogComponent implements OnChanges, OnInit {
    readonly visible = model<boolean>(false);
    readonly email = input.required<string>();

    private readonly fb = inject(FormBuilder).nonNullable;
    private readonly browserInfoService = inject(BrowserInfoService);
    private readonly reCaptchaV3Service = inject(ReCaptchaV3Service);
    private readonly route = inject(ActivatedRoute);
    private readonly accountService = inject(AccountService);
    private readonly linkNhsMiProService = inject(LinkNhsMiproService);
    private readonly router = inject(Router);
    private readonly userService = inject(UserService);
    private readonly securityService = inject(SecurityService);
    private reCaptchaKey = "";
    private invalid2FaCode = false;
    private organizationUrl = "";
    private nhsCode = "";

    protected readonly loginForm = this.fb.group({
        email: ["", [Validators.required, Validators.pattern(ValidationPatterns.notEmpty)]],
        password: ["", [Validators.required, Validators.maxLength(200)]],
        twoFactorCode: [""],
        browser: [this.browserInfoService.browserId],
        fingerprint: [this.browserInfoService.browserFingerprint]
    });
    protected readonly currentStep = signal<LinkNhsMiproStep>("password");
    protected readonly requestOngoing = signal(false);
    protected readonly errorMessage = signal("");
    protected readonly linkError = signal(false);
    protected readonly recaptchaMessageVisible = computed(() => this.currentStep() === "password" || this.currentStep() === "2fa");

    ngOnChanges(changes: SimpleChanges) {
        if (changes.visible?.currentValue) {
            this.reset();
        }

        if (changes.email) {
            this.loginForm.patchValue({ email: changes.email.currentValue });
        }
    }

    async ngOnInit() {
        this.reCaptchaKey = await this.accountService.getReCaptchaSiteKey();
        this.nhsCode = await fv(this.route.queryParams.pipe(map(p => p.verificationNhsCode)));
        this.organizationUrl = await fv(this.route.params.pipe(map(p => p.organizationurl)));
    }

    protected async login() {
        this.requestOngoing.set(true);
        this.errorMessage.set("");

        const loginInfo = this.createLoginInfo();

        if (this.reCaptchaKey) {
            const reCaptchaConfig = { useGlobalDomain: false } as RecaptchaConfiguration;
            loginInfo.captchaResponse = await this.reCaptchaV3Service.executeAsPromise(this.reCaptchaKey, "login", reCaptchaConfig);
        }

        const loginResult = await this.linkNhsMiProService.login(loginInfo);
        this.handleLoginResult(loginResult);

        this.requestOngoing.set(false);
    }

    private handleLoginResult(loginResult: LinkNhsMiproLoginResponse) {
        if (loginResult.loggedIn) {
            this.completeSelfRegistration();
        } else if (loginResult.invalidCredentials) {
            this.errorMessage.set("Provided password is incorrect.");
        } else if (loginResult.requires2FaCode) {
            if (this.invalid2FaCode) {
                this.errorMessage.set("Invalid 2FA code.");
            }
            this.loginForm.controls.twoFactorCode.setValidators(Validators.required);
            this.currentStep.set("2fa");
            this.invalid2FaCode = true;
        } else if (loginResult.unknownError) {
            this.errorMessage.set("An unknown error has occurred.");
        }
    }

    private async completeSelfRegistration() {
        this.currentStep.set("linking");
        this.requestOngoing.set(true);

        const result = await this.linkNhsMiProService.completeLinking({
            email: this.email(),
            nhsCode: this.nhsCode,
            organizationUrl: this.organizationUrl
        });

        if (result.success) {
            if(result.status === CompleteNhsLinkingStatus.IM1RegistrationFailed) {
                AppStorage.setItemForSession(AppStorage.HomeScreenMessages, {
                    warning: ["There was an issue connecting to your GP record. Please try again. If the problem persists, please refer to the Relevant Frequently Asked Questions (FAQ) section"]
                });
            }

            AppStorage.setItem(AppStorage.LoginInProcess, true);
            await this.userService.refreshCurrentUser();
            await this.securityService.refreshRoutes(true);
            await this.router.navigate(["/"]);
        } else {
            this.linkError.set(true);
            this.errorMessage.set(result.errorMessage);
            this.currentStep.set("none");
        }

        this.requestOngoing.set(false);
    }

    private createLoginInfo() {
        const info = Object.assign({}, this.loginForm.value) as LinkNhsMiproLoginRequest;
        info.password = CryptoES.SHA512(info.password).toString();
        info.captchaResponse = "";
        return info;
    }

    private reset() {
        this.loginForm.reset({
            email: this.email(),
            password: "",
            twoFactorCode: ""
        });
        this.loginForm.controls.twoFactorCode.clearValidators();
        this.loginForm.controls.twoFactorCode.reset();

        this.currentStep.set("password");
        this.errorMessage.set("");
        this.requestOngoing.set(false);
        this.linkError.set(false);
        this.invalid2FaCode = false;
    }
}

declare type LinkNhsMiproStep = "password" | "2fa" | "linking" | "none";
